import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import { Badge, Box, Icon, TabGroup, TabItem } from '@saviynt/design-system';
import classnames from 'classnames';

import useResponsive from '../../../hooks/use-responsive';
import { setActiveTab } from '../../../redux/certlist/action';
import { REF_PROP } from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import {
  CERTS_LIST_ALL_TAB,
  CERTS_LIST_CANCELED_TAB,
  CERTS_LIST_DONE_TAB,
  CERTS_LIST_EXPIRED_TAB,
  CERTS_LIST_TODO_TAB,
} from '../constant';

import { getTooltipText } from './utils';

import './CertsListHeaderTabs.css';

/**
 * CertsListHeaderTabs Component
 *
 * This component renders a tab group for managing the navigation between different
 * states or views in a certification list. It dynamically handles the active tab state,
 * dispatches actions to update the global state, and adjusts labels/icons based on
 * whether the user is an admin.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered CertsListHeaderTabs component.
 *
 * @example
 * <CertsListHeaderTabs />
 *
 * Behavior:
 * - The component manages the `activeTab` state through Redux, initializing it to a default value if not set.
 * - It uses a tab group to allow users to switch between different certification states (e.g., To-Do, Done, Canceled, Expired, All).
 * - Icons and labels for each tab adapt based on whether the user is logged in as an admin.
 * - A tooltip is provided for additional context regarding expired certifications.
 *
 * Key Features:
 * - **Dynamic Tab Management**: The `activeTab` is stored in the Redux state and updated via `handleChange`.
 * - **Admin-Specific Labels**: Tab labels adapt based on the `isAdminLogin` flag from the global state.
 * - **Icons for Context**: Each tab is paired with a specific icon to visually represent its purpose.
 * - **Tooltip**: Displays additional information about expired certifications with an info icon.
 *
 * Hooks:
 * - **useEffect**: Ensures the `activeTab` is initialized to the default `CERTS_LIST_TODO_TAB` if it is not already set.
 * - **useSelector**: Retrieves `isAdminLogin` and `activeTab` from the Redux state.
 * - **useDispatch**: Dispatches the `setActiveTab` action to update the Redux state when a tab is selected.
 *
 * Props:
 * - None (state and behavior are managed through Redux).
 *
 * Dependencies:
 * - **Redux**: The component interacts with the `certifications.list` slice of the Redux state.
 * - **CERTS_LIST_TAB_LABELS**: A map of labels for regular users.
 * - **CERTS_LIST_ADMIN_TAB_LABELS**: A map of labels for admin users.
 * - **CERTS_LIST_TODO_TAB, CERTS_LIST_DONE_TAB, CERTS_LIST_CANCELED_TAB, CERTS_LIST_EXPIRED_TAB, CERTS_LIST_ALL_TAB**: Constants defining the tab keys.
 *
 * Tabs Overview:
 * - **To-Do Tab**: Shows items pending action (`TimeLapse` icon).
 * - **Done Tab**: Displays completed certifications (`FilledCircle` icon).
 * - **Canceled Tab**: Lists certifications that were canceled (`HalfFilledCircle` icon).
 * - **Expired Tab**: Includes certifications that have expired (`FilledHexagon` icon).
 * - **All Tab**: Displays all certifications (no icon).
 *
 * Tooltip:
 * - Displays the message: "Some access was automatically revoked after the certification due date."
 * - Triggered by hovering over an info icon next to the expired badge.
 */
function CertsListHeaderTabs({ tabsAndControlsRef }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isMobileXlargeWidth } = useResponsive();

  const {
    global: { isTouchEnabled, isGlobalLoading },
    core: { isAdminLogin },
    tabs: { activeTab, tabsCount },
    sidePanel: { isSidePanelVisible },
  } = useSelector((state) => state?.certifications?.list);
  const { messages } = useSelector((state) => state?.user);

  const handleChange = (value) => {
    Logger.log('Loading data: ', isGlobalLoading);

    if (!isGlobalLoading) dispatch(setActiveTab(value));
  };

  const tabClasses = classnames(
    'CertsListHeaderTabs',
    isSidePanelVisible && 'CertsListHeaderTabs--isSidePanelVisible'
  );

  // LOGIC TO SET THE ACTIVE TAB ON TAB LOAD MOVED TO `packages/certs/src/page/CertificationList/CertificationList.js`

  return (
    <Box className={tabClasses}>
      <TabGroup
        value={activeTab}
        onChange={handleChange}
        isMobileWidth={isMobileXlargeWidth}
        moreContainerRef={tabsAndControlsRef}
        className='CertsListHeaderTabs-tabGroup'
        classNames={{
          fullWidthBorderClassName: 'CertsListHeaderTabs-tabGroup--fullWidth',
        }}>
        <TabItem
          label={
            messages.CERTS_TODO || localize(intl, getMessageKey('CERTS_TODO'))
          }
          secondaryLabel={getTooltipText(
            CERTS_LIST_TODO_TAB,
            isAdminLogin,
            messages,
            intl
          )}
          value={CERTS_LIST_TODO_TAB}
          icon={<Icon kind='StatusToDo' size='xSmall' color='info-700' />}
          isIconFillDisabled
          BadgeComp={
            <Badge
              badgeStyle={
                activeTab === CERTS_LIST_TODO_TAB ? 'filled' : 'outlined'
              }
              color='secondary'
              kind='number'
              size='medium'
              number={tabsCount[CERTS_LIST_TODO_TAB]}
            />
          }
          tooltipProps={
            isTouchEnabled
              ? {}
              : {
                  text: getTooltipText(
                    CERTS_LIST_TODO_TAB,
                    isAdminLogin,
                    messages,
                    intl
                  ),
                  maxWidth: '11rem',
                  isPortalEnabled: true,
                  className: 'CertsListHeaderTabs-tabGroup--tooltip',
                }
          }
        />
        <TabItem
          label={
            messages.CERTS_DONE || localize(intl, getMessageKey('CERTS_DONE'))
          }
          secondaryLabel={getTooltipText(
            CERTS_LIST_DONE_TAB,
            isAdminLogin,
            messages,
            intl
          )}
          value={CERTS_LIST_DONE_TAB}
          icon={
            <Icon kind='StatusCompleteFill' size='xSmall' color='success-700' />
          }
          isIconFillDisabled
          BadgeComp={
            <Badge
              badgeStyle={
                activeTab === CERTS_LIST_DONE_TAB ? 'filled' : 'outlined'
              }
              color='secondary'
              kind='number'
              size='medium'
              number={tabsCount[CERTS_LIST_DONE_TAB]}
            />
          }
          tooltipProps={
            isTouchEnabled
              ? {}
              : {
                  text: getTooltipText(
                    CERTS_LIST_DONE_TAB,
                    isAdminLogin,
                    messages,
                    intl
                  ),
                  maxWidth: '11rem',
                  isPortalEnabled: true,
                  className: 'CertsListHeaderTabs-tabGroup--tooltip',
                }
          }
        />
        {tabsCount[CERTS_LIST_CANCELED_TAB] > 0 && (
          <TabItem
            label={
              messages.CERTS_CANCELLED ||
              localize(intl, getMessageKey('CERTS_CANCELLED'))
            }
            secondaryLabel={getTooltipText(
              CERTS_LIST_CANCELED_TAB,
              isAdminLogin,
              messages,
              intl
            )}
            value={CERTS_LIST_CANCELED_TAB}
            icon={
              <Icon kind='StatusCanceled' size='xSmall' color='neutral-600' />
            }
            isIconFillDisabled
            BadgeComp={
              <Badge
                badgeStyle={
                  activeTab === CERTS_LIST_CANCELED_TAB ? 'filled' : 'outlined'
                }
                color='secondary'
                kind='number'
                size='medium'
                number={tabsCount[CERTS_LIST_CANCELED_TAB]}
              />
            }
            tooltipProps={
              isTouchEnabled
                ? {}
                : {
                    text: getTooltipText(
                      CERTS_LIST_CANCELED_TAB,
                      isAdminLogin,
                      messages,
                      intl
                    ),
                    maxWidth: '11rem',
                    isPortalEnabled: true,
                    className: 'CertsListHeaderTabs-tabGroup--tooltip',
                  }
            }
          />
        )}
        {tabsCount[CERTS_LIST_EXPIRED_TAB] > 0 && (
          <TabItem
            label={
              messages.CERTS_EXPIRED ||
              localize(intl, getMessageKey('CERTS_EXPIRED'))
            }
            secondaryLabel={getTooltipText(
              CERTS_LIST_EXPIRED_TAB,
              isAdminLogin,
              messages,
              intl
            )}
            value={CERTS_LIST_EXPIRED_TAB}
            icon={
              <Icon kind='StatusError' size='xSmall' color='critical-700' />
            }
            BadgeComp={
              <Badge
                badgeStyle={
                  activeTab === CERTS_LIST_EXPIRED_TAB ? 'filled' : 'outlined'
                }
                color='secondary'
                kind='number'
                size='medium'
                number={tabsCount[CERTS_LIST_EXPIRED_TAB]}
              />
            }
            tooltipProps={
              isTouchEnabled
                ? {}
                : {
                    text: getTooltipText(
                      CERTS_LIST_EXPIRED_TAB,
                      isAdminLogin,
                      messages,
                      intl
                    ),
                    maxWidth: '11rem',
                    isPortalEnabled: true,
                    className: 'CertsListHeaderTabs-tabGroup--tooltip',
                  }
            }
            isIconFillDisabled
          />
        )}
        <TabItem
          label={
            messages.CERTS_ALL || localize(intl, getMessageKey('CERTS_ALL'))
          }
          value={CERTS_LIST_ALL_TAB}
          BadgeComp={
            <Badge
              badgeStyle={
                activeTab === CERTS_LIST_ALL_TAB ? 'filled' : 'outlined'
              }
              color='secondary'
              kind='number'
              size='medium'
              number={tabsCount[CERTS_LIST_ALL_TAB]}
            />
          }
        />
      </TabGroup>
    </Box>
  );
}

CertsListHeaderTabs.propTypes = {
  tabsAndControlsRef: REF_PROP.isRequired,
};

CertsListHeaderTabs.defaultProps = {};

export default CertsListHeaderTabs;
